// Developer: Ankit Gupta
/**
 * Small presentation pieces shared by the whole product, so every screen in
 * the app is built from the same header, stat card and section shell.
 */
import type { ReactNode } from 'react';
import { Button, Space, Typography } from 'antd';

const { Text } = Typography;

/** Page title, one-line subtitle, and the page's primary actions. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 20,
        flexWrap: 'wrap',
        margin: '10px 0 22px',
      }}
    >
      <div>
        <h1 className="dp-page-title">{title}</h1>
        {subtitle && <p className="dp-page-sub">{subtitle}</p>}
      </div>
      {actions && <Space wrap>{actions}</Space>}
    </div>
  );
}

export function PrimaryButton(props: React.ComponentProps<typeof Button>) {
  const { className, ...rest } = props;
  return <Button {...rest} className={`dp-btn-primary ${className ?? ''}`} />;
}

export function GhostButton(props: React.ComponentProps<typeof Button>) {
  const { className, ...rest } = props;
  return <Button {...rest} className={`dp-btn-ghost ${className ?? ''}`} />;
}

/**
 * Stat card with the coloured wave filling its lower third.
 *
 * The wave is inline SVG rather than an image so it inherits the card's exact
 * width at any breakpoint and needs no asset to load.
 */
export function StatCard({
  label,
  value,
  caption,
  colour,
  icon,
  onClick,
}: {
  label: string;
  value: ReactNode;
  caption?: string;
  colour: string;
  icon?: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className="dp-stat"
      onClick={onClick}
      style={{
        borderColor: `color-mix(in srgb, ${colour} 30%, transparent)`,
        cursor: onClick ? 'pointer' : undefined,
      }}
    >
      <div className="dp-stat-label">
        <Space size={8}>
          {icon && <span style={{ color: colour }}>{icon}</span>}
          {label}
        </Space>
      </div>
      <div className="dp-stat-value">{value}</div>
      {/* The window this figure counts over, stated rather than implied. Four
          of these sit in a row counting over three different windows - a
          standing total, a rolling week, and all time - and an 11px grey
          caption was the only thing distinguishing them. */}
      {caption && <div className="dp-stat-caption">{caption}</div>}
      {/* There was a coloured wave here, shaped exactly like a sparkline and
          encoding nothing: the same curve on every card whatever the number.
          It spent the one visual convention that means "this is how the figure
          moved" on decoration, which teaches a reader to distrust the real
          sparklines elsewhere in the product. These counters have no series
          behind them, so the honest rendering is no chart at all. */}
    </div>
  );
}

/** A titled block with an optional right-hand action, used for page sections. */
export function Section({
  title,
  extra,
  children,
  style,
}: {
  title?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
  style?: React.CSSProperties;
}) {
  return (
    <section
      style={{
        background: 'var(--dp-surface)',
        border: '1px solid var(--dp-line)',
        borderRadius: 14,
        padding: 20,
        ...style,
      }}
    >
      {(title || extra) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div style={{ fontSize: 16, fontWeight: 600 }}>{title}</div>
          {extra}
        </div>
      )}
      {children}
    </section>
  );
}

/** Centred empty state, matching the muted tone used across the app. */
export function EmptyBlock({
  icon,
  title,
  description,
  action,
  compact,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  compact?: boolean;
}) {
  return (
    <div style={{ textAlign: 'center', padding: compact ? '28px 16px' : '52px 16px' }}>
      {icon && (
        <div
          style={{
            width: 54,
            height: 54,
            margin: '0 auto 14px',
            borderRadius: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--dp-violet-wash-soft)',
            color: 'var(--dp-violet-2)',
            fontSize: 22,
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{title}</div>
      {description && (
        <Text type="secondary" style={{ fontSize: 13, display: 'block', maxWidth: 420, margin: '0 auto' }}>
          {description}
        </Text>
      )}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

/** A dim label / bright value pair, used in the source and file detail lists. */
export function KeyValue({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '5px 0' }}>
      <Text type="secondary" style={{ fontSize: 12.5 }}>
        {label}
      </Text>
      <span style={{ fontSize: 12.5 }}>{value}</span>
    </div>
  );
}
